import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const jiraDataCenter = defineConnector({
  identifier: "jiraDataCenter",
  name: "Jira Data Center",
  tagLine: "Self-hosted issue tracking",
  logo,
  protocol: "http",
  tags: ["productivity", "it"],
  defaultUrlPattern: ({ baseUrl }) => domainToRegex(baseUrl),
  auth: oauthAuth,
  steps: [
    {
      title: "Enter your Jira Data Center instance details",
      items: [
        {
          kind: "markdown",
          content: "Enter the details for your Jira Data Center instance.",
        },
        {
          kind: "url",
          label: "Instance URL",
          placeholder: "https://jira.example.com",
          required: true,
          target: { kind: "configuration", field: "baseUrl" },
          helperText:
            "The URL you use to reach Jira in your browser, e.g. https://jira.example.com or https://jira.example.com/jira.",
        },
      ],
    },
    {
      title: "Create a Jira Data Center app",
      items: [
        {
          kind: "markdown",
          content:
            "Open the [Jira Data Center OAuth docs](https://developer.atlassian.com/server/jira/platform/oauth/) and register an application link in your Jira Data Center admin settings. Copy the client credentials and note the base URL for your Jira instance.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content: "In your app’s settings, add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/jiraDataCenter/callback",
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Authorize your Jira Data Center account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Jira Data Center account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: ({ baseUrl }) =>
      `${baseUrl}/rest/oauth2/latest/authorize`,
    tokenEndpoint: ({ baseUrl }) => `${baseUrl}/rest/oauth2/latest/token`,
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) => `${baseUrl}/rest/api/latest/`,
  },
  publicEnvVars: ({ baseUrl }) => ({
    JIRA_DATA_CENTER_ENDPOINT_URL: baseUrl || "",
  }),
});
